import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import Index from "./pages/Index";
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import Screen_Out from "./pages/Screen_Out";
import Player from "./pages/Player";
import StudioLab from "./pages/StudioLab";
import NotFound from "./pages/NotFound";
import AdminDashboard from "./admin-dashboard";

// Módulo de Motoristas (Drivers) - ISOLADO do sistema legado
import {
  DriverRegister,
  DriverLogin,
  DriverOnboarding,
  DriverRouteGuard,
  ScreenReviewApproved,
  ScreenCorrection,
  ScreenRejected,
  CompanionDashboard,
  DashCompanion
} from "@/features/drivers";

import MaintenancePage from "./pages/Maintenance";
import { useSystemSettings } from "@/hooks/useSystemSettings";

// ... (imports existentes)

console.log('[Main] 🚀 App Iniciado. URL Completa:', window.location.href);

const queryClient = new QueryClient();

// Componente para proteger rotas
const ProtectedRoute = ({
  children,
  checkMaintenance = false,
  adminOnly = false
}: {
  children: React.ReactNode,
  checkMaintenance?: boolean,
  adminOnly?: boolean
}) => {
  const { user, loading } = useAuth();
  const { settings, loading: settingsLoading } = useSystemSettings();

  console.log('🔒 ProtectedRoute - Verificando autenticação e manutenção:', {
    userId: user?.id,
    userEmail: user?.email,
    loading,
    hasUser: !!user,
    maintenanceMode: settings?.maintenance_mode,
    checkMaintenance,
    adminOnly
  });

  if (loading || settingsLoading) {
    console.log('🔒 ProtectedRoute - Carregando...');
    return <div className="flex items-center justify-center h-screen">Carregando...</div>;
  }

  if (!user) {
    console.log('🔒 ProtectedRoute - Usuário não autenticado, redirecionando para /');
    return <Navigate to="/" replace />;
  }

  // Verificação de segurança para Admin
  if (adminOnly) {
    const isLetsWatchEmail = user.email?.endsWith('@letswatch.com.br') || user.email === 'master@admin.com'; // Mantendo master@admin.com por compatibilidade

    if (!isLetsWatchEmail) {
      console.log('🔒 ProtectedRoute - Acesso negado: Usuário não possui domínio @letswatch.com.br');
      // Redireciona usuários não-admin para o dashboard comum
      return <Navigate to="/dashboard" replace />;
    }
  }

  // Verifica modo de manutenção
  if (checkMaintenance && settings?.maintenance_mode) {
    // Permite que o admin master acesse mesmo em manutenção, se desejar testar
    if (user.email !== 'master@admin.com' && !user.email?.endsWith('@letswatch.com.br')) {
      console.log('🔒 ProtectedRoute - Sistema em manutenção, exibindo tela de bloqueio');
      return <MaintenancePage />;
    }
  }

  console.log('🔒 ProtectedRoute - Acesso permitido');
  return <>{children}</>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          <Route path="/" element={<Auth />} />
          <Route path="/auth" element={<Navigate to="/" replace />} />
          <Route path="/paneladmin" element={<Index />} />
          <Route path="/landing" element={<Landing />} />
          <Route path="/dashboard" element={
            <ProtectedRoute checkMaintenance={true}>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/studiolab" element={
            <ProtectedRoute checkMaintenance={true}>
              <StudioLab />
            </ProtectedRoute>
          } />
          <Route path="/player" element={<Player />} />
          <Route path="/admin-dashboard" element={
            <ProtectedRoute checkMaintenance={false} adminOnly={true}>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/screen-out" element={<Screen_Out />} />

          {/* ============================================================ */}
          {/* ROTAS DE MOTORISTAS (DRIVERS) - ISOLADAS DO SISTEMA LEGADO */}
          {/* ============================================================ */}

          {/* Autenticação */}
          <Route path="/driver/register" element={<DriverRegister />} />
          <Route path="/driver/login" element={<DriverLogin />} />
          <Route path="/driver/onboarding" element={<DriverOnboarding />} />
          <Route path="/driver/review" element={<ScreenReviewApproved />} />
          <Route path="/driver/correction" element={<ScreenCorrection />} />
          <Route path="/driver/rejected" element={<ScreenRejected />} />

          {/* Companion (Celular do Motorista) */}
          <Route path="/companion" element={<CompanionDashboard />} />
          <Route path="/dash_companion" element={<DashCompanion />} />


          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
